import React from 'react';

/**
 * WorkflowStepper — horizontal progress visualization for the hr_v1 7-step
 * cycle. Used at the top of HrV1Console and as a compact bar in cycle list
 * rows so HR sees at a glance where each cycle is.
 *
 * Status meanings:
 *   done     — step finished, green check
 *   active   — current step, dark outlined ring
 *   pending  — not yet started
 *   optional — step that may or may not fire (Appeal)
 *   skipped  — step that won't fire (e.g. Step 6 skipped when no appeal)
 */

export type StepStatus = 'done' | 'active' | 'pending' | 'optional' | 'skipped';

export interface WorkflowStep {
  num: number;
  label: string;
  detail?: string;
  status: StepStatus;
  icon?: string;
}

interface WorkflowStepperProps {
  steps: WorkflowStep[];
  onStepClick?: (num: number) => void;
  /** Compact mode — 24px circles, hides detail text. For inline use in lists. */
  compact?: boolean;
}

const CIRCLE_CLASS: Record<StepStatus, string> = {
  done:     'bg-emerald-600 text-white border-emerald-600',
  active:   'bg-stone-900 text-white border-stone-900 ring-4 ring-stone-200',
  pending:  'bg-white text-stone-400 border-stone-300',
  optional: 'bg-amber-50 text-amber-700 border-amber-300',
  skipped:  'bg-stone-100 text-stone-400 border-stone-200',
};

const LABEL_CLASS: Record<StepStatus, string> = {
  done:     'text-stone-700',
  active:   'text-stone-900 font-semibold',
  pending:  'text-stone-400',
  optional: 'text-amber-700',
  skipped:  'text-stone-400 line-through',
};

const WorkflowStepper: React.FC<WorkflowStepperProps> = ({ steps, onStepClick, compact = false }) => {
  const circleSize = compact ? 'w-7 h-7 text-xs' : 'w-10 h-10 text-sm';
  const iconSize   = compact ? 14 : 18;

  return (
    <div className="flex items-stretch w-full overflow-x-auto pb-1">
      {steps.map((s, i) => {
        const isLast = i === steps.length - 1;
        const next   = steps[i + 1];
        const connectorDone = s.status === 'done' && (
          next?.status === 'done'
          || next?.status === 'active'
          || (next?.status === 'skipped')
        );
        return (
          <React.Fragment key={s.num}>
            <div className={`flex flex-col items-center ${compact ? 'min-w-[36px]' : 'min-w-[72px]'} flex-1`}>
              <button
                type="button"
                onClick={onStepClick ? () => onStepClick(s.num) : undefined}
                className={[
                  circleSize,
                  'rounded-full border-2 flex items-center justify-center font-semibold transition-all flex-shrink-0',
                  CIRCLE_CLASS[s.status],
                  onStepClick ? 'cursor-pointer hover:scale-110' : 'cursor-default',
                ].join(' ')}
                aria-label={`Step ${s.num}: ${s.label}`}
                title={`${s.label}${s.detail ? ` — ${s.detail}` : ''}`}
              >
                {s.status === 'done' ? (
                  <span className="material-symbols-rounded" style={{ fontSize: iconSize }}>check</span>
                ) : s.status === 'skipped' ? (
                  <span className="material-symbols-rounded" style={{ fontSize: iconSize }}>remove</span>
                ) : s.icon ? (
                  <span className="material-symbols-rounded" style={{ fontSize: iconSize }}>{s.icon}</span>
                ) : (
                  s.num
                )}
              </button>
              {!compact && (
                <>
                  <div className={`text-xs mt-2 text-center leading-tight ${LABEL_CLASS[s.status]}`}>
                    {s.label}
                  </div>
                  {s.detail && (
                    <div className="text-[10px] text-stone-500 text-center leading-tight mt-0.5">
                      {s.detail}
                    </div>
                  )}
                </>
              )}
            </div>
            {!isLast && (
              <div className="flex items-center flex-1 min-w-[16px]" aria-hidden="true">
                <div className={[
                  'h-0.5 w-full',
                  connectorDone ? 'bg-emerald-500' : 'bg-stone-200',
                ].join(' ')} />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default WorkflowStepper;
